import React, { useState, useCallback, useEffect } from 'react';
import {
	Avatar,
	Badge,
	Backdrop,
	Box,
	IconButton,
	CircularProgress,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
} from '@mui/material';
import { Cancel } from '@mui/icons-material';
import { makeStyles } from '@mui/styles';
import withStyles from '@mui/styles/withStyles';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';

import { ConfirmDialog, DropZone } from './../../common/components';
import {
	deleteAvatar,
	getUserAvatars,
	uploadAvatar,
} from './../../common/services/avatarsRequests';

const useStyles = makeStyles((theme) => ({
	// ... existing styles
	dialogContent: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		padding: theme.spacing(2),
	},
	dialogActions: {
		justifyContent: 'center',
		padding: theme.spacing(2),
	},
}));

const StyledBadge = withStyles((theme) => ({
	badge: {
		right: 4,
		top: 8,
		border: `0px solid ${theme.palette.background.paper}`,
		padding: '0',
	},
}))(Badge);

const AvatarSelectionDialog = ({ open, onClose, onSelect, selectedAvatarId }) => {
	const [avatars, setAvatars] = useState([]);
	const [loading, setLoading] = useState(false);
	const [avatarToDelete, setAvatarToDelete] = useState();
	const [showDeleteAvatar, setShowDeleteAvatar] = useState(false);
	const classes = useStyles();
	const { t } = useTranslation();

	const fetchAvatars = async () => {
		setLoading(true);
		const response = await getUserAvatars();
		setAvatars(response.avatars);
		setLoading(false);
	};

	const deleteAvatarById = async () => {
		setLoading(true);
		setShowDeleteAvatar(false);
		await deleteAvatar(avatarToDelete);
		await fetchAvatars();
		setLoading(false);
	};

	const onDrop = useCallback(async (file) => {
		setLoading(true);
		const newFile = await uploadAvatar(file.binary, file);
		await fetchAvatars();
		setLoading(false);
	}, []);

	useEffect(() => {
		fetchAvatars();
	}, []);

	return (
		<Dialog open={open}>
			<DialogTitle>{t('AvatarSelectionDialog.title')}</DialogTitle>
			<DialogContent className={classes.dialogContent}>
				<Box display="flex">
					{avatars.map((avatar) => (
						<IconButton
							key={avatar.ID}
							aria-label="avatar"
							size="large"
							onClick={() => onSelect(avatar)}>
							<StyledBadge
								badgeContent={
									<Cancel
										onClick={() => {
											setAvatarToDelete(avatar.ID);
											setShowDeleteAvatar(true);
										}}
									/>
								}
								color="secondary">
								<Avatar src={avatar.url} />
							</StyledBadge>
						</IconButton>
					))}
				</Box>
				<DropZone onFileReady={onDrop}></DropZone>
				<Backdrop className={classes.backdrop} open={loading}>
					<CircularProgress color="inherit" />
				</Backdrop>
				<ConfirmDialog
					open={showDeleteAvatar}
					setOpen={setShowDeleteAvatar}
					title={t('UserInfoSelector.deleteAvatarDialog.title')}
					text={t('UserInfoSelector.deleteAvatarDialog.text')}
					successButtonText={t('UserInfoSelector.deleteAvatarDialog.buttons.success')}
					cancelButtonText={t('UserInfoSelector.deleteAvatarDialog.buttons.cancel')}
					onSuccess={deleteAvatarById}></ConfirmDialog>
			</DialogContent>
			<DialogActions className={classes.dialogActions}>
				<Button variant="contained" onClick={onClose}>
					{t('AvatarSelectionDialog.cancelButton')}
				</Button>
				<Button variant="contained" color="primary" onClick={onClose}>
					{t('AvatarSelectionDialog.saveButton')}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default AvatarSelectionDialog;
